import {
  getDiagnosticAssessmentLabel,
  instantFromDate,
  instantToDate,
  isLessonLinkAvailable,
  isMakeupDiagnosticLesson,
  type DateRange,
} from '@airreading/common';
import { Temporal } from 'temporal-polyfill';
import { SessionStatus, VideoConferenceTypeLabels } from '@airreading/common/data';
import clsx from 'clsx';
import {
  type MouseEventHandler,
  type Ref,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { EventProps } from 'react-big-calendar';

import { useSession } from '@/auth/useSession';
import { useTimeZone } from '@/auth/useTimeZone';
import StudentsIcon from '@/assets/icons/union-icon.svg?react';
import { AlertBanner } from '@/components/alert-banner';
import ClipIcon from '@/components/assets/clip-icon.svg?react';
import DurationIcon from '@/components/assets/duration-icon.svg?react';
import { CopyableLink } from '@/components/copyable-link';
import { StartOrJoinSession } from '@/components/session/start-or-join-session';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators';
import { CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { MOBILE_MAX_WIDTH_MEDIA_QUERY, useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/tailwind-utils';
import { X } from 'lucide-react';
import { attendSession, recordSessionLessonLinkOpened } from '@/lib/api-calls';
import { EventDisplayTagBadge } from '@/routes/_layout/sessions/-components/event-display-tag-badge';
import { useMutation } from '@tanstack/react-query';

import { OnDemandGLALink } from './on-demand-gla-link';
import { SessionScheduleCell } from './session-schedule-cell';
import { EventContext } from './session-schedule-calendar-shared';
import type { ExpandedEvent } from './session-schedule-calendar-types';

const POPOVER_CONTENT_CLASS = 'session-schedule-event-popover z-40 p-0 m-0 w-[400px]';

const PANEL_WIDTH_PX = 400;
const PANEL_GAP_PX = 4;
const PANEL_VIEWPORT_MARGIN_PX = 8;

/**
 * Returns true when the anchor element is fully outside the visible rect of any
 * scroll/overflow ancestor. Used to hide the panel when the event block scrolls
 * out of the time-grid container.
 */
function isAnchorClippedByScrollParent(anchorEl: HTMLElement): boolean {
  const anchorRect = anchorEl.getBoundingClientRect();
  let el: HTMLElement | null = anchorEl.parentElement;
  while (el && el !== document.documentElement) {
    const { overflow, overflowY, overflowX } = getComputedStyle(el);
    const scrolls = [overflow, overflowY, overflowX].some((v) => v === 'auto' || v === 'scroll');
    if (scrolls) {
      const containerRect = el.getBoundingClientRect();
      if (anchorRect.bottom <= containerRect.top || anchorRect.top >= containerRect.bottom) {
        return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}

function computePanelFixedPosition(
  anchorEl: HTMLElement,
  panelEl: HTMLElement | null,
): { top: number; left: number } | null {
  if (isAnchorClippedByScrollParent(anchorEl)) {
    return null;
  }

  const anchorRect = anchorEl.getBoundingClientRect();
  const panelHeight = panelEl?.getBoundingClientRect().height ?? 0;

  // Prefer below the anchor; flip above when not enough space.
  let top = anchorRect.bottom + PANEL_GAP_PX;
  if (panelHeight > 0 && top + panelHeight > window.innerHeight - PANEL_VIEWPORT_MARGIN_PX) {
    const topAbove = anchorRect.top - panelHeight - PANEL_GAP_PX;
    if (topAbove >= PANEL_VIEWPORT_MARGIN_PX) {
      top = topAbove;
    }
  }

  const left = Math.min(
    Math.max(PANEL_VIEWPORT_MARGIN_PX, anchorRect.left),
    window.innerWidth - PANEL_WIDTH_PX - PANEL_VIEWPORT_MARGIN_PX,
  );

  return { top, left };
}

/**
 * Tracks the anchor element's fixed position via RAF so the panel follows it
 * as the user scrolls the time grid. Returns null while the anchor is hidden
 * behind a scroll container (panel should not render).
 */
function useCalendarPanelPosition(
  anchorElRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLDivElement | null>,
  enabled: boolean,
): { top: number; left: number } | null {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const rafIdRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) {
      setPosition(null);
      return;
    }

    const tick = () => {
      const anchor = anchorElRef.current;
      const pos = anchor ? computePanelFixedPosition(anchor, panelRef.current) : null;
      // Avoid re-renders when position hasn't changed.
      setPosition((prev) => {
        if (prev === null && pos === null) return prev;
        if (prev?.top === pos?.top && prev?.left === pos?.left) return prev;
        return pos;
      });
      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [enabled, anchorElRef, panelRef]);

  return position;
}

/**
 * Portal-based panel that renders at a fixed position computed from the anchor
 * element's bounding rect. Follows the anchor during scroll and disappears
 * (without closing) when the anchor scrolls out of its clipping container.
 */
function CalendarEventFloatingPanel({
  event,
  anchorElRef,
  open,
  panelRef,
  onCloseButtonClick,
}: {
  event: ExpandedEvent;
  anchorElRef: RefObject<HTMLElement | null>;
  open: boolean;
  panelRef: RefObject<HTMLDivElement | null>;
  onCloseButtonClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const position = useCalendarPanelPosition(anchorElRef, panelRef, open);

  if (!open || !position) {
    return null;
  }

  return createPortal(
    <div
      ref={panelRef as Ref<HTMLDivElement>}
      data-state="open"
      className={cn(
        POPOVER_CONTENT_CLASS,
        'rounded-md border bg-popover text-popover-foreground shadow-md outline-none',
      )}
      style={{ position: 'fixed', top: position.top, left: position.left }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <SessionScheduleEventPopoverContent event={event} onCloseButtonClick={onCloseButtonClick} />
    </div>,
    document.body,
  );
}

/** Matches {@link EventScheduleAnchor} / legacy RBC anchors for outside-click handling. */
const SESSION_SCHEDULE_EVENT_SELECTOR = '[data-session-schedule-event-id]';

/** Stable date suffix for calendar event test ids (UTC civil day of `event.start`). */
function calendarEventTestIdDate(event: ExpandedEvent): string {
  if (!event.start) {
    return 'unknown';
  }
  return new Date(event.start).toISOString().split('T')[0]!;
}

function useDismissPopoverOnOutsideClick(
  isOpen: boolean,
  onDismiss: () => void,
  popoverContentRef: RefObject<HTMLDivElement | null>,
  options?: { ignoreRbcEventClicks?: boolean; ignoreScheduleXEventClicks?: boolean },
) {
  const { ignoreRbcEventClicks = false, ignoreScheduleXEventClicks = false } = options ?? {};

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (mouseEvent: MouseEvent) => {
      const target = mouseEvent.target as HTMLElement;

      if (popoverContentRef.current?.contains(target)) {
        return;
      }

      if (ignoreRbcEventClicks && target.closest('.rbc-event')) {
        return;
      }

      if (ignoreScheduleXEventClicks && target.closest(SESSION_SCHEDULE_EVENT_SELECTOR)) {
        return;
      }

      onDismiss();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onDismiss, popoverContentRef, ignoreRbcEventClicks, ignoreScheduleXEventClicks]);
}

//#region Event block UI (RBC anchor + Schedule-X grid cell)

/** Inner labels for an event card (shared by RBC PopoverAnchor and Schedule-X wrapper). */
export function EventScheduleAnchorContent({
  event,
  variant = 'default',
}: {
  event: ExpandedEvent;
  variant?: 'default' | 'month';
}) {
  const isMonth = variant === 'month';

  return (
    <>
      <span
        className={clsx('whitespace-nowrap', isMonth ? 'text-[11px] leading-tight shrink-0' : 'text-base leading-none')}
      >
        {event.timeRangeFormatted}
      </span>
      <div
        className={clsx(
          'w-full overflow-hidden whitespace-normal',
          isMonth
            ? 'text-xs font-semibold leading-tight line-clamp-1'
            : clsx(
                'text-base font-semibold leading-none',
                event.durationMinutes >= 40 ? 'line-clamp-2' : 'line-clamp-1',
              ),
        )}
      >
        {event.title}
      </div>
    </>
  );
}

/** Event block in the grid (Schedule-X uses one shared popover at calendar root). */
export function EventScheduleAnchor({
  event,
  variant = 'default',
}: {
  event: ExpandedEvent;
  variant?: 'default' | 'month';
}) {
  if (!event.start) {
    return null;
  }

  const eventIdentifier = calendarEventTestIdDate(event);
  const isMonth = variant === 'month';

  return (
    <div
      className={clsx(
        'w-full flex flex-col items-start justify-start min-w-0',
        isMonth ? 'h-auto gap-px py-0.5' : 'h-full',
      )}
      title="Click to view class details"
      data-testid={`sessions-calendar-event-${eventIdentifier}`}
      data-session-schedule-event-id={event.id}
    >
      <EventScheduleAnchorContent event={event} variant={variant} />
    </div>
  );
}

//#endregion

//#region Popover body (shared by RBC EventWrapper and Schedule-X root popover)

function SessionDurationLabel(range: DateRange) {
  const [start, end] = [instantFromDate(range.start), instantFromDate(range.end)];
  const { minutes } = start.until(end).round({ largestUnit: 'minute' });
  return <>{minutes} minutes</>;
}

function SessionScheduleEventPopoverContent({
  event,
  onCloseButtonClick,
}: {
  event: ExpandedEvent;
  onCloseButtonClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const { sessionById, substitutionRequestSummaryBySessionId } = useContext(EventContext);
  const { actingUser } = useSession();
  const { timeZone } = useTimeZone();

  const { mutateAsync: attendSessionMutate } = useMutation({
    mutationFn: attendSession,
  });

  const session = sessionById.get(event.id);
  if (!session || !event.start) {
    return null;
  }

  const { startInstant, endInstant, orgName, onDemandGLAs } = session;
  const sessionHasSomeoneWithOnDemandGLA = onDemandGLAs.length > 0;
  const isMoreThan8HoursInPast = Temporal.Instant.compare(endInstant.add({ hours: 8 }), Temporal.Now.instant()) < 0;
  const eventIdentifier = calendarEventTestIdDate(event);
  const isMakeupDiagnostic =
    isMakeupDiagnosticLesson(session.lesson.name) &&
    session.students.some((s) => s.needsDiagnosticAssessment !== undefined);
  const classPlatform = session.videoConferenceType ? VideoConferenceTypeLabels[session.videoConferenceType] : null;
  const coachIdWhoIsTeaching = substitutionRequestSummaryBySessionId.get(session.id)?.coachIdWhoIsTeaching;
  const sessionUrl = session[session.videoConferenceType]?.hostUrl ?? session[session.videoConferenceType]?.attendeeUrl;

  const onStartOrJoinSessionButtonClick = async () => {
    await attendSessionMutate({ id: session.id });
  };

  return (
    <CardContent
      className="p-6 flex flex-row-reverse gap-1 justify-between"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="h-fit [&_svg]:w-6 [&_svg]:h-6 [&_svg]:text-[#8C8C8C]"
        onClick={onCloseButtonClick}
        data-testid={`sessions-calendar-button-close-${eventIdentifier}`}
      >
        <X className="h-6 w-6" />
        <span className="sr-only">Close</span>
      </button>
      <div className="flex flex-col gap-3">
        {session.coachSessionRemarksV2.length > 0 && (
          <div className="flex flex-wrap gap-2.5">
            {[...session.coachSessionRemarksV2]
              .sort((a, b) => a.localeCompare(b))
              .map((tag) => (
                <EventDisplayTagBadge key={tag} tag={tag} session={session} />
              ))}
          </div>
        )}
        <SessionScheduleCell
          className="text-xl font-bold"
          startInstant={startInstant}
          endInstant={endInstant}
          displayTimeZone={timeZone}
          showWeekday
          data-testid={`sessions-calendar-event-time-${eventIdentifier}`}
        />
        <div className="flex items-start gap-2" data-testid={`sessions-calendar-event-students-${eventIdentifier}`}>
          <StudentsIcon />
          <div>
            {[...session.students]
              .sort((a, b) => (a.coachFacingDisplayName ?? '').localeCompare(b.coachFacingDisplayName ?? ''))
              .map((s) => {
                const studentDisplayName = s.coachFacingDisplayName ?? '';
                const diagnosticLabel = isMakeupDiagnostic
                  ? getDiagnosticAssessmentLabel(s.needsDiagnosticAssessment)
                  : null;
                const onDemandGLA = session.onDemandGLAs.find(({ studentId }) => studentId === s.id);
                return (
                  <div key={s.id} className="flex gap-x-1 items-center justify-start">
                    {studentDisplayName} {diagnosticLabel}{' '}
                    {onDemandGLA && (
                      <OnDemandGLALink
                        onDemandGLA={onDemandGLA}
                        grade={s.grade}
                        dataTestId={`sessions-calendar-event-on-demand-gla-lesson-${eventIdentifier}`}
                      />
                    )}
                    <StudentNeedsIndicators specialEd={s.specialEd} dyslexic={s.dyslexic} ell={s.ell} mode="icons" />
                  </div>
                );
              })}
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid={`sessions-calendar-event-duration-${eventIdentifier}`}>
          <DurationIcon />
          <SessionDurationLabel start={instantToDate(startInstant)} end={instantToDate(endInstant)} />
        </div>
        <div className="flex items-center gap-2">
          <ClipIcon />
          {isLessonLinkAvailable(session.lessonLink, startInstant) ? (
            <CopyableLink
              className="text-air-primary"
              externalHref={session.lessonLink!}
              canCopyUrl={false}
              showExternalHrefIcon={false}
              onExternalClick={() => {
                void recordSessionLessonLinkOpened(session.id).catch(() => {});
              }}
              data-testid={`sessions-calendar-event-lesson-${eventIdentifier}`}
            >
              {session.lesson.name}
            </CopyableLink>
          ) : (
            <span data-testid={`sessions-calendar-event-lesson-${eventIdentifier}`}>{session.lesson.name}</span>
          )}
        </div>
        {sessionHasSomeoneWithOnDemandGLA && (
          <AlertBanner
            title="Please have both assessment and lesson ready before class, and assess student first."
            content={null}
          />
        )}
        {coachIdWhoIsTeaching === actingUser.id && session.status !== SessionStatus.cancelled && (
          <StartOrJoinSession
            label={`Start ${classPlatform ?? 'Class'}`}
            url={sessionUrl}
            disabledTooltip={isMoreThan8HoursInPast ? 'Past sessions can no longer be started' : undefined}
            startButtonProps={{
              className: 'bg-air-primary hover:bg-air-primary/80',
              icon: undefined,
              onClick: onStartOrJoinSessionButtonClick,
              'data-testid': `sessions-calendar-button-start-${eventIdentifier}`,
            }}
            whenUrlEmptyRender={`No ${classPlatform ?? 'meeting'} url`}
          />
        )}
        {orgName && <div data-testid={`sessions-calendar-event-district-${eventIdentifier}`}>{orgName}</div>}
      </div>
    </CardContent>
  );
}

//#region Schedule-X root popover (single portal)

/** Single popover for Schedule-X (avoids duplicate portals from date-grid + time-grid). */
export function SessionScheduleEventPopover({
  event,
  anchorEl,
  open,
  onClose,
}: {
  event: ExpandedEvent;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}) {
  const isMobile = useMediaQuery(MOBILE_MAX_WIDTH_MEDIA_QUERY);
  const panelRef = useRef<HTMLDivElement>(null);
  const anchorElRef = useRef<HTMLElement | null>(anchorEl);
  anchorElRef.current = anchorEl;

  const didClosePopover = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (mouseEvent) => {
      mouseEvent.stopPropagation();
      onClose();
    },
    [onClose],
  );

  useDismissPopoverOnOutsideClick(open && !isMobile, onClose, panelRef, {
    ignoreScheduleXEventClicks: true,
  });

  if (!open) {
    return null;
  }

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <SheetContent
          side="bottom"
          hideCloseButton
          className="max-h-[90dvh] rounded-t-xl border-0 p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetTitle className="sr-only">Class details</SheetTitle>
          <SessionScheduleEventPopoverContent event={event} onCloseButtonClick={didClosePopover} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <CalendarEventFloatingPanel
      event={event}
      anchorElRef={anchorElRef}
      open={open}
      panelRef={panelRef}
      onCloseButtonClick={didClosePopover}
    />
  );
}

//#endregion

//#region react-big-calendar EventWrapper (legacy; per-event popover)

export function EventWrapper({ event }: EventProps & { event: ExpandedEvent }) {
  const { activeEventId, clearActiveEvent, sessionById } = useContext(EventContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const anchorElRef = useRef<HTMLElement | null>(null);

  const didClosePopover = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (mouseEvent) => {
      // `onSelectEvent` will trigger again unless stopped here.
      mouseEvent.stopPropagation();
      clearActiveEvent();
    },
    [clearActiveEvent],
  );

  const isOpen = activeEventId === event.id;
  anchorElRef.current = anchorRef.current;

  useDismissPopoverOnOutsideClick(isOpen, clearActiveEvent, panelRef, { ignoreRbcEventClicks: true });

  if (!sessionById.get(event.id) || !event.start) {
    return null;
  }

  const eventIdentifier = calendarEventTestIdDate(event);

  return (
    <>
      <div
        ref={anchorRef}
        className="w-full h-full flex flex-col items-start justify-start"
        title="Click to view class details"
        data-testid={`sessions-calendar-event-${eventIdentifier}`}
      >
        <span className="whitespace-nowrap">{event.timeRangeFormatted}</span>
        <div
          className={clsx(
            'w-full font-semibold overflow-hidden whitespace-normal',
            event.durationMinutes >= 40 ? 'line-clamp-2' : 'line-clamp-1',
          )}
        >
          {event.title}
        </div>
      </div>
      <CalendarEventFloatingPanel
        event={event}
        anchorElRef={anchorElRef}
        open={isOpen}
        panelRef={panelRef}
        onCloseButtonClick={didClosePopover}
      />
    </>
  );
}

//#endregion
